import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FormContainer } from '../login/mfa/MFA.styles';
import { RegisterTitle } from './Register.styles';
import useRegisterConfirmSubmit from './useRegisterConfirmSubmit';
import MFACodeForm from '@/components/mfaForm/MFACodeForm';
import { Form, FormProvider } from '@/components/forms/form/Form';
import { HelperTextMuted } from '@/components/mfaForm/MFACodeForm.styles';
import { RegisterUserConfirmationMethod } from '@/services/register.types.ts';

interface RegisterConfirmationFormProps {
  anonymizedConfirmationCodeRecipient: string;
  confirmationMethod: RegisterUserConfirmationMethod;
  registrationToken: string;
}

const confirmationSchema = Yup.object({
  confirmationCode: Yup.string().length(6).required(),
});

// Lives inside the FormProvider so useRegisterConfirmSubmit can read the RHF
const ConfirmationForm = ({
  anonymizedConfirmationCodeRecipient,
  confirmationMethod,
  registrationToken,
}: RegisterConfirmationFormProps) => {
  const { t } = useTranslation();
  const {
    handleSubmitConfirm,
    handleResendCode,
    isSubmitting,
    isResending,
    resendCooldownSeconds,
  } = useRegisterConfirmSubmit({ registrationToken });

  return (
    <Form onSubmit={handleSubmitConfirm}>
      <RegisterTitle data-testid="register-confirm-title">
        {t('pageRegister.confirmTitle', { context: confirmationMethod })}
      </RegisterTitle>
      <MFACodeForm
        fieldName="confirmationCode"
        helperText={
          <HelperTextMuted>
            {t('pageRegister.confirmSubtitle')}{' '}
            <span>{anonymizedConfirmationCodeRecipient}</span>
          </HelperTextMuted>
        }
        isSubmitting={isSubmitting}
        isResending={isResending}
        handleResendCode={handleResendCode}
        resendCooldownSeconds={resendCooldownSeconds}
        submitLabel={t('pageRegister.confirm')}
      />
    </Form>
  );
};

const RegisterConfirmationForm = (props: RegisterConfirmationFormProps) => (
  <FormContainer>
    <FormProvider
      resolver={confirmationSchema}
      defaultValues={{ confirmationCode: '' }}
    >
      <ConfirmationForm {...props} />
    </FormProvider>
  </FormContainer>
);

export default RegisterConfirmationForm;
